/**
 * Repository for Execution Results
 * 
 * Handles CRUD operations for workflow execution results
 */

import BaseRepository, { FilterParams, PaginationParams, SortParams } from './BaseRepository';
import { ExecutionResult } from '../types/WorkflowEntities';

export class ExecutionResultRepository extends BaseRepository<ExecutionResult> {

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<ExecutionResult | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'ExecutionResult',
            criticalityLevel: 'MEDIUM', // Execution results are important for debugging
            metadata: { id, executionResultRepository: true }
        });

        const query = `
            query GetExecutionResultByTenantAndId($id: String!, $tenant: String!) {
                getExecutionResult(id: $id, tenant: $tenant) {
                    id instanceId status result error duration
                    startedAt completedAt
                    metrics {
                        stepCount errorCount retryCount memoryUsage cpuTime
                    }
                }
            }
        `;

        try {
            const result = await this.query<{ getExecutionResult: ExecutionResult | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 60 * 1000 // 1 minute cache for execution results
            });

            const executionResult = result.getExecutionResult || null;

            // ✅ FERP SAFETY: Log successful operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'ExecutionResult',
                    entityId: id,
                    found: !!executionResult,
                    executionStatus: executionResult?.status,
                    executionDuration: executionResult?.duration,
                    timestamp: Date.now()
                });
            }

            return executionResult;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'ExecutionResult',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    async findAll(params?: {
        filter?: FilterParams;
        pagination?: PaginationParams;
        sort?: SortParams;
    }): Promise<ExecutionResult[]> {
        const query = `
            query ListExecutionResults($tenant: String!, $filter: ExecutionResultFilter, $pagination: PaginationInput, $sort: SortInput) {
                listExecutionResults(tenant: $tenant, filter: $filter, pagination: $pagination, sort: $sort) {
                    items {
                        id instanceId status result error duration
                        startedAt completedAt
                        metrics {
                            stepCount errorCount retryCount memoryUsage cpuTime
                        }
                    }
                    total hasNext
                }
            }
        `;

        const response = await this.query(
            query,
            {
                tenant: this.tenant,
                filter: params?.filter,
                pagination: params?.pagination || { offset: 0, limit: 50 },
                sort: params?.sort
            },
            {
                cache: true,
                cacheTime: 30000 // 30 seconds
            }
        );

        return response.listExecutionResults?.items || [];
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(id: string): Promise<ExecutionResult | null> {
        return this.findByTenantAndId(this.tenant, id);
    }

    // Legacy method for reference
    async findByIdLegacy(id: string): Promise<ExecutionResult | null> {
        const query = `
            query GetExecutionResult($tenant: String!, $id: String!) {
                getExecutionResult(tenant: $tenant, id: $id) {
                    id instanceId status result error duration
                    startedAt completedAt
                    metrics {
                        stepCount errorCount retryCount memoryUsage cpuTime
                    }
                }
            }
        `;

        const response = await this.query(
            query,
            { tenant: this.tenant, id },
            {
                cache: true,
                cacheTime: 60000 // 1 minute
            }
        );

        return response.getExecutionResult || null;
    }

    async create(data: Partial<ExecutionResult>): Promise<ExecutionResult> {
        const mutation = `
            mutation CreateExecutionResult($tenant: String!, $input: CreateExecutionResultInput!) {
                createExecutionResult(tenant: $tenant, input: $input) {
                    id instanceId status startedAt
                }
            }
        `;

        const response = await this.mutation(
            mutation,
            {
                tenant: this.tenant,
                input: data
            },
            {
                onSuccess: () => {
                    this.cache.invalidate('ListExecutionResults');
                }
            }
        );

        return response.createExecutionResult;
    }

    async update(id: string, data: Partial<ExecutionResult>): Promise<ExecutionResult> {
        const mutation = `
            mutation UpdateExecutionResult($tenant: String!, $id: String!, $input: UpdateExecutionResultInput!) {
                updateExecutionResult(tenant: $tenant, id: $id, input: $input) {
                    id status completedAt result error
                }
            }
        `;

        const response = await this.mutation(
            mutation,
            {
                tenant: this.tenant,
                id,
                input: data
            },
            {
                onSuccess: () => {
                    this.cache.invalidate(`GetExecutionResult:${id}`);
                    this.cache.invalidate('ListExecutionResults');
                }
            }
        );

        return response.updateExecutionResult;
    }

    async delete(id: string): Promise<boolean> {
        const mutation = `
            mutation DeleteExecutionResult($tenant: String!, $id: String!) {
                deleteExecutionResult(tenant: $tenant, id: $id)
            }
        `;

        const response = await this.mutation(
            mutation,
            {
                tenant: this.tenant,
                id
            },
            {
                onSuccess: () => {
                    this.cache.invalidate(`GetExecutionResult:${id}`);
                    this.cache.invalidate('ListExecutionResults');
                }
            }
        );

        return response.deleteExecutionResult;
    }

    /**
     * Find execution results by instance ID
     */
    async findByInstanceId(instanceId: string): Promise<ExecutionResult[]> {
        return this.findAll({
            filter: { instanceId },
            sort: { field: 'startedAt', order: 'desc' }
        });
    }

    /**
     * Get execution statistics
     */
    async getStatistics(): Promise<any> {
        const query = `
            query GetExecutionStatistics($tenant: String!) {
                getExecutionStatistics(tenant: $tenant) {
                    totalExecutions
                    successRate
                    averageDuration
                    statusBreakdown {
                        status
                        count
                    }
                }
            }
        `;

        const response = await this.query(
            query,
            { tenant: this.tenant },
            {
                cache: true,
                cacheTime: 60000 // 1 minute
            }
        );

        return response.getExecutionStatistics;
    }
}

// Export singleton instance
export const executionResultRepository = new ExecutionResultRepository();

export default ExecutionResultRepository;